import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ConfigService, Globals, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { ignoreFalsy, LoadableHelper, Logger } from '@aiwyn/shared';
import { defer, EMPTY, interval, merge, Observable, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, retry, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  ROUTE_TOKEN_CXP_TASKS,
  ROUTE_TOKEN_ENGAGEMENTS_BASE,
  ROUTE_TOKEN_FILES,
  ROUTE_TOKEN_INVOICES,
  ROUTE_TOKEN_MAIN_BASE,
  ROUTE_TOKEN_PAYMENTS,
  ROUTE_TOKEN_PROJECTS,
  ROUTE_TOKEN_REVIEW_AND_PAY,
  ROUTE_TOKEN_SETTINGS,
} from '../routing-constants';
import { ScriptLoaderService } from './script-loader.service';
import { ChatbotApiService, MintResponse } from './chatbot-api.service';
import { AuthService } from '../auth/services/auth.service';

/**
 * Loads the Zendesk Messaging snippet (per-tenant config gated, like Pendo/LogRocket
 * own their snippets) and keeps conversationFields stamped with tenant context.
 *
 * Zendesk only applies conversationFields to a ticket when they are set before the
 * conversation's first message, so fields are applied as soon as the snippet loads
 * and re-applied on every navigation. [AIW-735]
 *
 * Once a user is signed in it authenticates the messenger via `loginUser` with a
 * server-minted JWT (AIW-1319): the JWT comes from artengine's secure-client
 * chatbot mint, which signs a trusted `external_id` (`aiwyn:<tenant>:<contactId>`)
 * derived from the verified session. The mint writes nothing to Zendesk — the bot
 * reads the tenant back out of `external_id` and resolves the firm's tier from the
 * Zendesk org record.
 * Both failure shapes leave the widget anonymous — fail-open to today's behaviour:
 * an ineligible session (e.g. a firm user) returns 200 with a `null` JWT, while an
 * unconfigured signing key returns an empty-bodied 503 that lands in the error path.
 *
 * Which snippet loads is per-tenant: when the `enable_advanced_ai_chatbot` LD flag is
 * on for the firm and a `cp_live_chat_src_advanced` snippet is configured, the new
 * Advanced AI agent's widget loads in place of the Essential one — otherwise the
 * Essential widget loads unchanged (fail-safe no-op). [AIW-1723]
 *
 * This service should eventually become a `Tracker` adapter so identify/reset flow
 * through `TrackService` like the other vendor integrations.
 */
@Injectable({
  providedIn: 'root',
})
export class ZendeskWidgetService {
  private static readonly CONFIG_PLACEHOLDER = 'SET-ME';

  // The messaging widget cannot re-authenticate mid-session: when its JWT expires
  // it drops the conversation to anonymous even if the loginUser refresh callback
  // supplies a fresh token (verified against the dev widget, AIW-1723). Re-running
  // the full loginUser before expiry is the only thing that keeps a long-lived tab
  // authenticated, so re-login on a cadence comfortably inside the 1h token TTL.
  private static readonly RELOGIN_INTERVAL_MS = 30 * 60 * 1000;

  // Backoff for the residual boot race: a mint fired while the Firebase token is
  // still hydrating goes out without a Bearer — the auth interceptor's token
  // loadable is still LOADING, so it attaches only the Tenant header — and 401s
  // at artengine (AIW-1723). The last retry lands well past any plausible hydration.
  private static readonly MINT_RETRY_DELAYS_MS = [2000, 8000, 30000] as const;

  private readonly _class = 'ZendeskWidgetService';

  // Zendesk's snippet installs `zE` as an untyped window global; one accessor
  // keeps the unavoidable cast in a single place (cf. PendoService.pendoAgent).
  private get zE(): ((...args: unknown[]) => void) | undefined {
    return (window as { zE?: (...args: unknown[]) => void }).zE;
  }

  // aiwynhelp.zendesk.com custom ticket field ids — one Zendesk instance serves all
  // portal environments, so these are not per-environment config.
  private readonly fieldIds = {
    tenantId: '51589270110619',
    portalType: '51589297691931',
    featureContext: '51589273327259',
    firmName: '53599210627355',
    entitlements: '53599461683355',
    pmSystem: '53599465875099',
    clientIds: '54222358434843',
  };

  // Plain comma-join (not the padded analytics format): agents read this field and
  // paste ids into admin lookups. Client-supplied context for triage only — never a gate.
  private clientIdsValue = '';

  // Real client ids are positive DB keys; the quick-payments session carries the sentinel
  // -1 (see auth-guard), which would otherwise stamp a useless "-1" for agents to chase.
  private positiveClientIds(ids: number[] | undefined): string {
    return (ids ?? []).filter(id => id > 0).join(',');
  }

  // Config booleans surfaced to agents as a comma-separated entitlements token list.
  // Client-supplied (spoofable) context for triage only — never a gate. [AIW-1723]
  private readonly entitlementFlags: readonly { readonly token: string; readonly enabled: () => boolean }[] = [
    { token: 'payments', enabled: () => this.configService.enableClientPortalPayments },
    { token: 'autopay', enabled: () => this.configService.autoPayEnabled },
    { token: 'engagements', enabled: () => this.configService.engagementsEnabled },
    { token: 'cxp', enabled: () => this.configService.clientExperiencePortalEnabled },
    { token: 'practice_os', enabled: () => this.configService.practiceOsEnabled },
  ];

  // Feature routes live under /main (ROUTE_TOKEN_MAIN_BASE); quick-pay uses a
  // top-level segment. Values reuse the routing tokens so route renames stay in sync.
  private readonly featureAreas: readonly string[] = [
    ROUTE_TOKEN_PROJECTS,
    ROUTE_TOKEN_FILES,
    ROUTE_TOKEN_INVOICES,
    ROUTE_TOKEN_PAYMENTS,
    ROUTE_TOKEN_REVIEW_AND_PAY,
    ROUTE_TOKEN_ENGAGEMENTS_BASE,
    ROUTE_TOKEN_SETTINGS,
    ROUTE_TOKEN_CXP_TASKS,
  ];

  constructor(
    private readonly configService: ConfigService,
    private readonly scriptLoader: ScriptLoaderService,
    private readonly globals: Globals,
    private readonly router: Router,
    private readonly logger: Logger,
    private readonly chatbotApi: ChatbotApiService,
    private readonly launchDarkly: LaunchDarklyService,
    private readonly authService: AuthService,
  ) {
    this.bootstrap();
  }

  private bootstrap(): void {
    this.configService.configValuesLoaded$
      .pipe(
        ignoreFalsy(),
        take(1),
        filter(() => this.configService.liveChatEnabled),
        switchMap(() =>
          this.launchDarkly.getFeatureFlag(LaunchDarklyFlag.ENABLE_ADVANCED_AI_CHATBOT).pipe(take(1))),
        map(advancedEnabled => this.resolveLiveChatSrc(advancedEnabled)),
        switchMap(src =>
          this.scriptLoader.loadScript(src, false, [{ attribute: 'id', value: 'ze-snippet' }])),
        // loadScript reports onerror as next(false) and re-emits whenever any other
        // script's status changes; only the first successful load should start us.
        filter(didLoad => {
          if (!didLoad) {
            this.logger.error(`${this._class} :: bootstrap :: live chat script failed to load`);
          }
          return didLoad;
        }),
        take(1),
      )
      .subscribe(() => this.start());
  }

  /**
   * Pick which Zendesk messaging widget to load. The new Advanced AI agent runs on
   * its own Zendesk channel/widget, so swapping to it is just loading a different
   * snippet. Gated behind the per-tenant `enable_advanced_ai_chatbot` flag AND a
   * configured `cp_live_chat_src_advanced`; either one missing — including the
   * `SET-ME` provisioning placeholder, which would otherwise load as a dead script
   * URL and take chat down entirely — falls back to the current (Essential) widget,
   * so the default is a no-op.
   */
  private resolveLiveChatSrc(advancedEnabled: boolean): string {
    const advancedSrc = this.configService.liveChatSrcAdvanced;
    if (advancedEnabled && advancedSrc && advancedSrc !== ZendeskWidgetService.CONFIG_PLACEHOLDER) {
      this.logger.info(`${this._class} :: resolveLiveChatSrc :: advanced AI chatbot enabled, loading advanced widget`);
      return advancedSrc;
    }
    return this.configService.liveChatSrc;
  }

  // Invoked at most once — bootstrap()'s trailing take(1) guarantees a single start,
  // so re-emissions from the shared ScriptLoaderService can't stack router subscriptions.
  private start(): void {
    this.logger.info(`${this._class} :: start :: live chat script loaded, starting conversation context`);
    // Minting is driven off auth state, never fired blindly at script load: a
    // boot-time mint races Firebase token hydration and 401s tokenless
    // (AIW-1723) — see authenticate().
    this.authenticate();

    // Emits immediately (undefined pre-login), then on every auth change — so fields
    // stamp at startup and re-stamp once login resolves the user's client list.
    let wasAuthenticated = false;
    this.authService.getUser(true).subscribe(user => {
      // A sign-out on a shared browser must not leave the messenger authenticated as the
      // previous user: Zendesk persists the loginUser session in browser storage, so without
      // an explicit logout the next user inherits the prior identity and chat history.
      if (wasAuthenticated && !user) {
        this.logoutMessenger();
      }
      wasAuthenticated = !!user;
      this.clientIdsValue = this.positiveClientIds(user?.getClientIdList());
      this.setConversationFields();
    });

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.setConversationFields());
  }

  /**
   * Drives messenger authentication from auth state, not script load. At script
   * load the Firebase token is usually not hydrated yet, so a boot-time mint
   * goes out without a Bearer and 401s — and a previously persisted loginUser
   * session masks the loss, leaving the widget
   * silently anonymous for up to a full re-login interval (AIW-1723, confirmed
   * on authenticated prod sessions). A signed-in user — including the signed-out
   * -> signed-in transition — mints immediately and then on the re-login cadence.
   * Signed out there is nothing to mint, so the cadence stops (the boot-time
   * mint 401'd fleet-wide on the login page, pure noise) and any in-flight mint
   * is cancelled so the next user can't be logged in with the previous one's JWT.
   *
   * Each mint still waits for the login sequence to settle — LOADED or ERROR,
   * never LOADING. Secure-link entry runs signOut() -> loginAnonymously() ->
   * validateSecureLink() (login-strategy-factory.service.ts); minting while
   * that's mid-flight lands in a window with no usable Firebase user and 401s
   * outright (ART-8792) — a race, not a real auth rejection. This does not
   * change who is eligible for an identified session, only when we ask.
   *
   * One stream owns every trigger, so concurrent runs are structurally
   * impossible: the outer switchMap tears down the cadence and any in-flight
   * mint when the signed-in state flips, and the inner switchMap collapses
   * stacked ticks — or a tick racing a still-pending settle gate — into the
   * latest one.
   */
  private authenticate(): void {
    this.authService.getUser(true).pipe(
      map(user => !!user),
      distinctUntilChanged(),
      switchMap(signedIn => !signedIn
        ? EMPTY
        : merge(of(0), interval(ZendeskWidgetService.RELOGIN_INTERVAL_MS)).pipe(
          switchMap(() => this.authService.getUserLoadable().pipe(
            filter(({ status }) => LoadableHelper.isLoaded(status, true)),
            take(1),
            switchMap(() => this.mintWithRetry()),
          )),
        )),
    ).subscribe(({ jwt }) => this.loginMessenger(jwt));
  }

  /**
   * Mint a Zendesk Messaging JWT for the verified session, retrying failures on
   * a short backoff: in the residual race where a user exists but the Firebase
   * token is still hydrating, the mint goes out tokenless and 401s, and the
   * request never gets another chance until the next re-login
   * tick. A 200 with a null JWT is a definitive answer — ineligible session,
   * stay anonymous by design — never a failure, so it is not retried. Exhausted
   * retries leave the widget anonymous — fail-open, never blocks chat.
   */
  private mintWithRetry(): Observable<MintResponse> {
    // defer makes every retry a fresh mint call rather than a re-subscription of
    // the first request observable.
    return defer(() => this.chatbotApi.mintMessagingJwt()).pipe(
      retry({
        count: ZendeskWidgetService.MINT_RETRY_DELAYS_MS.length,
        delay: (error, retryCount) => {
          this.logger.warn(`${this._class} :: mintWithRetry :: mint attempt ${retryCount} failed; retrying`, error);
          return timer(ZendeskWidgetService.MINT_RETRY_DELAYS_MS[retryCount - 1]);
        },
      }),
      catchError(error => {
        this.logger.error(`${this._class} :: mintWithRetry :: mint failed after retries; staying anonymous`, error);
        return EMPTY;
      }),
    );
  }

  /**
   * Authenticate the messenger via `loginUser` with a minted JWT. A null JWT
   * (ineligible session / server unconfigured) leaves the widget anonymous —
   * never blocks chat.
   *
   * Zendesk re-invokes the `loginUser` callback whenever it needs a fresh token,
   * so any conversation outliving the JWT hits a refresh mid-session. Replaying
   * the first token would silently de-authenticate the session at expiry
   * (messages stall in "Sending"), so only the first invocation uses the
   * already-minted token; every later one mints anew.
   */
  private loginMessenger(jwt: string | null): void {
    if (!jwt) {
      this.logger.info(`${this._class} :: loginMessenger :: no jwt; staying anonymous`);
      return;
    }
    const zE = this.zE;
    if (!zE) {
      this.logger.warn(`${this._class} :: loginMessenger :: zE not present after script load`);
      return;
    }
    let initialJwt: string | null = jwt;
    try {
      zE('messenger', 'loginUser', (callback: (token: string) => void) => {
        if (initialJwt) {
          const token = initialJwt;
          initialJwt = null;
          callback(token);
          return;
        }
        // The refresh mint must not outlive the session: getUser(true) replays the
        // current state, so takeUntil cancels synchronously when the user is already
        // signed out and cancels mid-flight on a later sign-out — a fresh JWT landing
        // after logoutUser would hand Zendesk the previous user's identity right back.
        this.chatbotApi.mintMessagingJwt().pipe(
          takeUntil(this.authService.getUser(true).pipe(filter(user => !user))),
        ).subscribe({
          next: ({ jwt: freshJwt }) => {
            if (!freshJwt) {
              this.logger.info(`${this._class} :: loginMessenger :: refresh mint returned no jwt; session de-authenticates`);
              return;
            }
            callback(freshJwt);
          },
          error: error => this.logger.error(`${this._class} :: loginMessenger :: refresh mint failed`, error),
        });
      });
      this.logger.info(`${this._class} :: loginMessenger :: messenger loginUser invoked`);
    } catch (error) {
      this.logger.error(`${this._class} :: loginMessenger :: zE loginUser threw`, error);
    }
  }

  // Ends the messenger's persisted loginUser session so a subsequent user on the same
  // browser starts anonymous instead of inheriting the previous user's Zendesk identity.
  // Known gap: only an in-page transition is observable — a sign-out this service never
  // witnessed (session expired while the tab was closed, sign-out before the snippet
  // loaded) leaves the persisted identity in place. Logging out on every signed-out
  // boot is not an option: it would wipe anonymous visitors' conversations on reload.
  private logoutMessenger(): void {
    const zE = this.zE;
    if (!zE) {
      this.logger.warn(`${this._class} :: logoutMessenger :: zE not present after script load`);
      return;
    }
    try {
      zE('messenger', 'logoutUser');
      this.logger.info(`${this._class} :: logoutMessenger :: messenger logoutUser invoked`);
    } catch (error) {
      this.logger.error(`${this._class} :: logoutMessenger :: zE logoutUser threw`, error);
    }
  }

  private setConversationFields(): void {
    const zE = this.zE;
    if (!zE) {
      this.logger.warn(`${this._class} :: setConversationFields :: zE not present after script load`);
      return;
    }

    try {
      // Zendesk drops conversationFields whose value is empty/undefined, so only
      // include the firm-context fields when the config actually carries a value.
      const fields: { id: string; value: string }[] = [
        { id: this.fieldIds.tenantId, value: this.globals.anTenant },
        { id: this.fieldIds.portalType, value: 'client_portal' },
        { id: this.fieldIds.featureContext, value: this.currentFeatureArea() },
      ];
      const firmName = this.configuredValue('firmName', this.configService.customerName);
      if (firmName) {
        fields.push({ id: this.fieldIds.firmName, value: firmName });
      }
      const pmSystem = this.configuredValue('pmSystem', this.configService.pmSystem);
      if (pmSystem) {
        fields.push({ id: this.fieldIds.pmSystem, value: pmSystem });
      }
      if (this.clientIdsValue) {
        fields.push({ id: this.fieldIds.clientIds, value: this.clientIdsValue });
      }
      const entitlements = this.currentEntitlements();
      if (entitlements) {
        fields.push({ id: this.fieldIds.entitlements, value: entitlements });
      }

      zE('messenger:set', 'conversationFields', fields);
    } catch (error) {
      this.logger.error(`${this._class} :: setConversationFields :: zE threw`, error);
    }
  }

  /**
   * A config value fit to show a support agent: unset and the `SET-ME` provisioning
   * placeholder (ConfigOption.ConfigPlaceholderValue server-side) both mean "not
   * configured yet" — a mid-onboarding firm must not file tickets reading `SET-ME`.
   * Omissions are debug-logged because a mid-apply config failure silently drops
   * every value at once.
   */
  private configuredValue(fieldName: string, value: string | undefined): string | undefined {
    if (!value || value === ZendeskWidgetService.CONFIG_PLACEHOLDER) {
      this.logger.debug(`${this._class} :: setConversationFields :: omitting unconfigured ${fieldName}`);
      return undefined;
    }
    return value;
  }

  // Comma-separated list of the firm's enabled products, for agent triage.
  // Empty string when nothing is enabled, so the caller omits the field entirely.
  private currentEntitlements(): string {
    return this.entitlementFlags
      .filter(flag => flag.enabled())
      .map(flag => flag.token)
      .join(',');
  }

  /**
   * Coarse feature area from the current URL — never the raw URL, which can carry
   * identifiers. Authenticated routes nest under /main, so the base segment is skipped.
   */
  private currentFeatureArea(): string {
    const segments = this.router.url
      .split(/[?#;]/)[0]
      .split('/')
      .filter(segment => segment.length > 0 && segment !== ROUTE_TOKEN_MAIN_BASE);
    const first = segments[0] ?? '';
    return this.featureAreas.includes(first) ? first : 'other';
  }
}
